import { useState, useEffect, useCallback } from "react";
import { useRoomStore } from "../store/roomStore";
import Room from "../components/Room";
import RoomDetails from "./RoomDetails";
import { SpinnerDotted } from "spinners-react";

export default function Rooms() {
  const { rooms, loading, fetchRooms } = useRoomStore();
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Handle hash-based navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith("#room-")) {
        const id = Number(hash.replace("#room-", ""));
        if (!isNaN(id)) {
          setSelectedRoomId(id);
        }
      } else {
        setSelectedRoomId(null);
      }
    };

    // Initial check
    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Handle room selection
  const handleRoomSelect = useCallback((roomId: number) => {
    window.location.hash = `room-${roomId}`;
    setSelectedRoomId(roomId);
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    window.location.hash = "";
    setSelectedRoomId(null);
  }, []);

  // If room is selected, show details
  if (selectedRoomId) {
    return (
      <RoomDetails
        roomId={selectedRoomId}
        onBack={handleBack}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center bg-gray-50/50">
        <div className="text-center">
          <SpinnerDotted 
            size={60} 
            thickness={150} 
            speed={120} 
            color="#3b82f6" 
          />
          <p className="mt-4 font-secondary text-gray-500">
            Loading rooms...
          </p>
        </div>
      </section>
    );
  }

  // Empty state
  if (rooms.length === 0) {
    return (
      <section className="py-24 bg-gray-50/50">
        <div className="container mx-auto max-w-7xl px-4 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md mx-auto">
            <div className="text-6xl mb-6">🏠</div>
            <h3 className="font-primary text-2xl font-bold text-gray-800 mb-2">
              No Rooms Available
            </h3>
            <p className="font-secondary text-gray-500">
              We couldn't find any rooms matching your criteria.
            </p>
            <p className="font-secondary text-sm text-gray-400 mt-2">
              Please try adjusting your search filters.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Room grid
  return (
    <section id="rooms" className="py-24 bg-gray-50/50">
      <div className="container mx-auto max-w-7xl px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="font-tertiary uppercase text-sm tracking-[6px] text-gray-500 font-semibold">
            Hotel & Spa Adina
          </p>
          <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mt-2">
            Our Rooms & Suites
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mt-4 rounded-full"></div>
          <p className="font-secondary text-gray-500 mt-4 max-w-2xl mx-auto">
            Discover our carefully curated selection of luxurious rooms designed for your comfort and relaxation.
          </p>
        </div>

        {/* Room Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rooms.map((room) => (
            <Room
              key={room.id}
              room={room}
              onSelect={() => handleRoomSelect(room.id)}
            />
          ))}
        </div>

        {/* Room Count */}
        <div className="mt-12 text-center">
          <p className="font-secondary text-sm text-gray-400">
            Showing {rooms.length} room{rooms.length > 1 ? 's' : ''} available
          </p>
        </div>
      </div>
    </section>
  );
}